import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { Loader2 } from 'lucide-react'
import api from '@/lib/api/axios'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { escapeHtml } from '@/lib/escape-html'

interface VaultSummaryEntry {
    vaultName: string
    vaultID?: string
    locationName?: string | null
    region?: string | null
    anomalyCount: number
    highSeverityCount?: number
    objectCount?: number
}

/** A row of the summary, whichever dimension it is grouped by. */
interface SummaryRow {
    name: string
    anomalyCount: number
    highSeverityCount: number
    objectCount: number
}

export function VaultLocationTab() {
    const { t } = useTranslation()

    const { data: entries = [], isLoading } = useQuery({
        queryKey: ['analytics', 'vault-summary'],
        queryFn: async () => {
            const res = await api.get('/api/analytics/vault-summary')
            return (res.data?.data || res.data || []) as VaultSummaryEntry[]
        },
    })

    // The endpoint returns one row per (vault, region, location), so both
    // views come from the same fetch — locations are the same anomalies
    // aggregated by site rather than by vault.
    const [dimension, setDimension] = useState<'vault' | 'location'>('vault')

    const rows = useMemo((): SummaryRow[] => {
        const acc = new Map<string, SummaryRow>()
        for (const e of entries) {
            const name = dimension === 'vault'
                ? (e.vaultName || 'Unknown')
                : (e.locationName || e.region || t('analytics.unknownLocation', 'Unknown location'))
            const row = acc.get(name) || { name, anomalyCount: 0, highSeverityCount: 0, objectCount: 0 }
            row.anomalyCount += Number(e.anomalyCount) || 0
            row.highSeverityCount += Number(e.highSeverityCount) || 0
            row.objectCount += Number(e.objectCount) || 0
            acc.set(name, row)
        }
        return [...acc.values()].sort((a, b) => b.anomalyCount - a.anomalyCount)
    }, [entries, dimension, t])

    const chartOption = useMemo((): EChartsOption => {
        if (rows.length === 0) return {}

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const p = Array.isArray(params) ? params[0] : params
                    const entry = rows[p.dataIndex]
                    if (!entry) return ''
                    let html = `<div style="font-weight:600">${escapeHtml(entry.name)}</div>`
                    html += `<div>Anomalies: <b>${entry.anomalyCount}</b></div>`
                    html += `<div>High Severity: <b>${entry.highSeverityCount}</b></div>`
                    html += `<div>Objects: <b>${entry.objectCount}</b></div>`
                    return html
                },
            },
            grid: { left: 50, right: 20, top: 20, bottom: 60 },
            xAxis: {
                type: 'category',
                data: rows.map((e) => e.name),
                axisLabel: { fontSize: 10, color: '#888', rotate: 30 },
                axisTick: { show: false },
                axisLine: { show: false },
            },
            yAxis: {
                type: 'value',
                name: 'Anomaly Count',
                nameTextStyle: { fontSize: 10, color: '#888' },
                axisLabel: { fontSize: 10, color: '#888' },
                splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } },
            },
            series: [
                {
                    type: 'bar',
                    data: rows.map((e) => e.anomalyCount),
                    itemStyle: {
                        color: '#3b82f6',
                        borderRadius: [4, 4, 0, 0],
                    },
                    barMaxWidth: 50,
                },
            ],
        }
    }, [rows])

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle>{t('analytics.vaultLocation', 'Vault & Location Summary')}</CardTitle>
                {/* The same anomalies, grouped either by the vault holding them
                    or by the site they came from — locations were previously
                    invisible even though the data carried them. */}
                <div className="flex rounded-md border p-0.5">
                    <button
                        type="button"
                        className={`rounded px-3 py-1 text-xs font-medium ${dimension === 'vault' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setDimension('vault')}
                    >
                        {t('analytics.byVault', 'Vaults')}
                    </button>
                    <button
                        type="button"
                        className={`rounded px-3 py-1 text-xs font-medium ${dimension === 'location' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setDimension('location')}
                    >
                        {t('analytics.byLocation', 'Locations')}
                    </button>
                </div>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        {t('analytics.loadingSummary', 'Loading summary…')}
                    </div>
                ) : entries.length === 0 ? (
                    <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
                        {t('analytics.noSummary', 'No summary data available.')}
                    </div>
                ) : (
                    <>
                        <ReactECharts option={chartOption} style={{ height: 350 }} opts={{ renderer: 'canvas' }} />

                        <div className="mt-6 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b text-left text-muted-foreground">
                                        <th className="pb-2 pr-4 font-medium">
                                            {dimension === 'vault' ? t('analytics.vault', 'Vault') : t('analytics.location', 'Location')}
                                        </th>
                                        <th className="pb-2 pr-4 font-medium">Anomalies</th>
                                        <th className="pb-2 pr-4 font-medium">High Severity</th>
                                        <th className="pb-2 font-medium">Objects</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((e) => (
                                        <tr key={e.name} className="border-b last:border-0">
                                            <td className="py-2 pr-4 font-medium">{e.name}</td>
                                            <td className="py-2 pr-4">{e.anomalyCount}</td>
                                            <td className="py-2 pr-4">{e.highSeverityCount}</td>
                                            <td className="py-2">{e.objectCount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    )
}
